import { useId, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { Alert } from '../../atoms/Alert/Alert';
import { Button } from '../../atoms/Button/Button';
import { Inline } from '../../atoms/Inline/Inline';
import { Input } from '../../atoms/Input/Input';
import { Select } from '../../atoms/Select/Select';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography/Text';
import { Dialog } from '../../ui/Dialog/Dialog';
import type { TestMapping, TestMappingTarget } from '../../../services/testMapping';
import {
  NEW_PARAMETER, draftTarget, formatValue, parseScale, targetLabel, type MappingDraft,
} from './testMappingDraft';
import './TestMapping.css';

export interface TestMappingDialogProps {
  row: TestMapping;
  targets: readonly TestMappingTarget[];
  initial: MappingDraft;
  onCancel: () => void;
  onSubmit: (draft: MappingDraft) => void;
  isSubmitting: boolean;
  submitError: boolean;
}

/**
 * Where one device test goes in ReefGuide — like the controller's "Wert
 * zuordnen" dialog: a parameter (or a new one, or "do not import") and the
 * factor every value is multiplied with, with the latest value converted live.
 */
export function TestMappingDialog({ row, targets, initial, onCancel, onSubmit, isSubmitting, submitError }: TestMappingDialogProps) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [draft, setDraft] = useState<MappingDraft>(initial);
  const [touched, setTouched] = useState(false);

  const byId = new Map(targets.map((target) => [target.parameterId, target]));
  const creating = draft.parameterId === NEW_PARAMETER;
  const target = draftTarget(draft, byId);
  const scale = parseScale(draft.scale);
  const nameMissing = creating && (draft.customName ?? '').trim() === '';
  const options = [
    { value: '', label: t('integrations.testMapping.ignore') },
    { value: NEW_PARAMETER, label: t('integrations.testMapping.createNew') },
    ...targets.map((tg) => ({ value: tg.parameterId, label: targetLabel(t, tg) })),
  ];

  let preview: string;
  if (!target) preview = nameMissing ? t('integrations.testMapping.customNameMissing') : t('integrations.testMapping.notImported');
  else if (row.latestValue === null || scale === null) preview = t('integrations.testMapping.resultUnknown');
  else preview = t('integrations.testMapping.result', { value: formatValue(row.latestValue * scale, target.unit, i18n.language) });

  const submit = () => {
    setTouched(true);
    if (isSubmitting || nameMissing || (target && scale === null)) return;
    onSubmit(draft);
  };

  return (
    <Dialog isOpen onClose={onCancel} title={t('integrations.testMapping.dialogTitle')}>
      <form noValidate onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <Stack gap="4">
          <Text color="muted" className="test-mapping__key">
            {row.reportedUnit ? `${row.sourceName} (${row.reportedUnit})` : row.sourceName}
          </Text>
          <Select
            id={`${id}-target`}
            label={t('integrations.testMapping.target')}
            value={draft.parameterId}
            options={options}
            onChange={(e) => {
              const parameterId = e.target.value;
              // A new parameter starts with the test's own name and unit, ready to adjust.
              setDraft(parameterId === NEW_PARAMETER
                ? { ...draft, parameterId, customName: draft.customName ?? row.sourceName, customUnit: draft.customUnit ?? row.reportedUnit }
                : { ...draft, parameterId });
            }}
            fullWidth
          />
          {creating && (
            <>
              <Input
                id={`${id}-custom-name`}
                label={t('integrations.testMapping.customName')}
                value={draft.customName ?? ''}
                maxLength={60}
                error={(touched || draft.customName !== undefined) && nameMissing ? t('integrations.testMapping.customNameError') : undefined}
                onChange={(e) => { setDraft({ ...draft, customName: e.target.value }); }}
                fullWidth
              />
              <Input
                id={`${id}-custom-unit`}
                label={t('integrations.testMapping.customUnit')}
                value={draft.customUnit ?? ''}
                maxLength={20}
                onChange={(e) => { setDraft({ ...draft, customUnit: e.target.value }); }}
                fullWidth
              />
            </>
          )}
          {(target || creating) && (
            <Input
              id={`${id}-scale`}
              label={t('integrations.testMapping.scale')}
              helperText={t('integrations.testMapping.scaleHint')}
              inputMode="decimal"
              value={draft.scale}
              error={scale === null ? t('integrations.testMapping.scaleError') : undefined}
              onChange={(e) => { setDraft({ ...draft, scale: e.target.value }); }}
              fullWidth
            />
          )}
          <Text variant="body-sm" color="secondary">
            {row.latestValue !== null
              ? `${t('integrations.testMapping.current', { value: formatValue(row.latestValue, row.reportedUnit, i18n.language) })} → ${preview}`
              : preview}
          </Text>
          {submitError && <Alert>{t('integrations.testMapping.saveError')}</Alert>}
          <Inline justify="end" gap="2">
            <Button type="button" variant="secondary" onClick={onCancel} disabled={isSubmitting}>
              {t('integrations.testMapping.cancel')}
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting} aria-busy={isSubmitting}>
              {t('integrations.testMapping.apply')}
            </Button>
          </Inline>
        </Stack>
      </form>
    </Dialog>
  );
}
